import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, inject, OnInit, Renderer2, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import _ from 'lodash';
import { BehaviorSubject, combineLatest, debounceTime, forkJoin, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { getResourceCategoryIconPath } from 'src/app/common/categoryUtils';
import { Stack } from 'src/app/common/dataStructures/stack';
import { formatFromDate } from 'src/app/common/datetime_utility';
import { getPlatformLogoPath } from 'src/app/common/platformUtils';
import { getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { owaspLcncToDisplayName } from 'src/app/common/violationRulesUtils';
import { RowActionClicked } from 'src/app/components/table-row/table-row.component';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import {
  ResetViolationsStatusApiModel,
  UpdatableViolationStatus,
  UpdateViolationsStatusApiModel,
  ViolationChangeJustificationApiModel,
  ViolationModel,
  ViolationsActionBulkApiModel,
  ViolationsDataType,
  ViolationsStatusUpdateResponse,
  ViolationStatus,
} from 'src/app/models/api/violations.model';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { FilterTypesEnum } from 'src/app/models/filters/filterTypes.enum';
import { ViolationsFiltersModel } from 'src/app/models/filters/implementations/pageFilters/violationsFilters.model';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { MenuAction } from 'src/app/models/menuAction.model';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { IChangeEventJustificationComponentOutput } from 'src/app/models/popups/outputs/changeEventJustificationComponentOutput.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import { ViolationsTableColumnsModel } from 'src/app/models/tableColumns/violationsTableColumn.model';
import { IViolationsViewModel } from 'src/app/models/viewModels/violationsViewModel';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { getViolationStatusBulkActionType } from 'src/app/services/actions/violations/helpers';
import { ViolationsActionsService } from 'src/app/services/actions/violations/violations-actions.service';
import { ViolationsSupportedActions } from 'src/app/services/actions/violations/violationsSupportedActions';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { ViolationsHttpService } from 'src/app/services/httpServices/violationsHttpService/violations-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { PermissionsService } from 'src/app/services/infra/permissionsService/permissions.service';
import { FieldName, TextProviderService } from 'src/app/services/infra/textProviderService/text-provider.service';
import { InventoryHelper } from '../inventory/helpers/inventoryHelper';
import { ViolationRowHelpers } from './helpers/violationRowHelpers';
import { ChangeEventJustificationDialogComponent } from '../../components/note-dialog/change-event-justification-dialog.component';
import { populateErrorMessage } from '../utils/utils';
import { DisplayNameService } from 'src/app/services/common/displayName/displayName.service';
import { buildResourceTypeDisplayName } from '../../labels/utils';
import { ColumnsOrderService } from 'src/app/services/common/columnsOrder/columns-order.service';
import { TableColumnsNames } from 'src/app/models/tableColumns/tableColumnsNames.enum';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SidePanelService } from '../../features/side-panel/services/side-panel.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { AccountCategory } from 'src/app/models/api/accountManagement.model';
import { AccountService } from 'src/app/services/infra/account/account.service';
import { AccessService } from 'src/app/services/infra/access/access.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { ZenAIDomain } from 'src/app/models/api/zenAi.model';

@Component({
  selector: 'app-violations',
  templateUrl: './violations.component.html',
  styleUrls: ['./violations.component.scss'],
})
export class ViolationsComponent implements OnInit {
  public columns = signal<ITableColumn[]>([]);
  public pageName = TableColumnsNames.Violations;
  public violationsCurrentPage$: BehaviorSubject<IViolationsViewModel[]>;
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public showNoData = false;

  public isFiltersMenuOpen = false;

  public expandFilterResultControl = new FormControl(false);
  public readonly defaultPageSize: number;
  public totalItems = 0;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;

  public filterEnums = FilterTypesEnum;
  public optionsAreSelected = false;

  public checkedViolationIds: Set<string> = new Set();
  public multiSelectedMenuActions$: BehaviorSubject<MenuAction[]> = new BehaviorSubject<MenuAction[]>([]);
  public multiActionsIsLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public allRowsSelected = new FormControl(false);

  public pageFilters: ViolationsFiltersModel;
  public sorts?: SortColumn[];
  public violationsDataType = ViolationsDataType.violations;
  public featureFlagTypes = FeatureFlagTypes;
  public permissionsEnum = PermissionEnum;
  public readonly zenAIDomain = ZenAIDomain;

  public accountCategory$: Observable<AccountCategory | null>;
  public AccountCategory = AccountCategory;

  public accessService = inject(AccessService);

  private _currentViolationsById: { [violationId: string]: ViolationModel } = {};

  private _paginationNavigation: IPaginationNavigationModel;

  private _firstPageSubscription$: Subscription;
  private _fetchByPageSubscription$: Subscription;
  private readonly _notAvailableString: string;

  private readonly _storeService = inject(StoreService);
  private readonly _accountService = inject(AccountService);
  private readonly _httpClient = inject(HttpClient);
  private _rsaMockViolations: ViolationModel[] | null = null;
  private _iamDemoMockViolations: ViolationModel[] | null = null;
  private _mockViolationIds: Set<string> = new Set();
  private _isOnFirstPage = true;

  constructor(
    public matDialog: MatDialog,
    public logger: LoggerService,
    public featureFlagsService: FeatureFlagsService,
    private readonly _sidePanel: SidePanelService,
    private readonly _violationsHttpService: ViolationsHttpService,
    private readonly _filtersService: FiltersService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _violationsActionsService: ViolationsActionsService,
    private readonly _notificationService: NotificationsService,
    private readonly _permissionsService: PermissionsService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _textProviderService: TextProviderService,
    private readonly _displayNameService: DisplayNameService,
    private readonly _columnsOrderService: ColumnsOrderService,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef
  ) {
    this.defaultPageSize = this._violationsHttpService.defaultPageSize;
    this._notAvailableString = this._labelsProviderPipe.transform('common.notAvailable');
    this._initializePageInfo();
  }

  ngOnInit(): void {
    this.accountCategory$ = this._accountService.getAccountCategory$();

    this.violationsCurrentPage$ = this._storeService.getViolationsPageDataAsSubject$();

    this.columns.set(
      this._columnsOrderService.getColumnsOrderFromLocalStorage(this.pageName, ViolationsTableColumnsModel)
    );

    this.pageFilters = this._filtersService.getFilters(PageWithFiltersEnum.Violations) as ViolationsFiltersModel;
    this.pageFilters.restoreSortColumnToDefaultIfNeeded();

    this._subscribeToFiltersAndSortChanges();

    this.allRowsSelected.valueChanges.pipe(tap((value) => this._onAllRowsSelectionClick(value))).subscribe();

    this.expandFilterResultControl.valueChanges.subscribe(() => this._updateSupportedActionsForSelectedRows());

    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  public rowSelected(selection: IViolationsViewModel): void {
    this._sidePanel.openViolationDetails(selection.violation.id, selection.integrationId, selection);
  }

  public rowUnselected = () => this._sidePanel.close();

  public tablesFiltersChange(selectedOptions: ISelectOption[]): void {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public filtersOnMenuStateChange(menuState: boolean): void {
    this.isFiltersMenuOpen = menuState;
  }

  public actionOnClick(actionClicked: RowActionClicked): void {
    const violationRow = actionClicked.row.getValue() as IViolationsViewModel;

    const response = this._violationsActionsService.handleActionClicked(
      actionClicked.action.name as ViolationsSupportedActions,
      this._currentViolationsById[violationRow.id]
    ) as Observable<ViolationsStatusUpdateResponse | undefined | boolean>;

    switch (actionClicked.action.name) {
      case ViolationsSupportedActions.Exempt:
      case ViolationsSupportedActions.Waive:
        this._handleViolationStatusUpdateActionResponse(
          response as Observable<ViolationsStatusUpdateResponse | undefined>,
          actionClicked
        );
        break;
      case ViolationsSupportedActions.Delete:
        this._handleResourceDeleteActionResponse(response as Observable<boolean>);
        break;
      default:
        response.pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
        break;
    }
  }

  public onPagination(violation: PaginationOperationEnum): void {
    if (!this.isLoading$.value) {
      if (violation == PaginationOperationEnum.next) {
        this._paginationNavigation.previousPage.push(this._paginationNavigation.currentPage);
      }

      const pageUrl =
        violation == PaginationOperationEnum.next
          ? this._paginationNavigation.nextPage
          : this._paginationNavigation.previousPage.pop();

      this._isOnFirstPage =
        violation !== PaginationOperationEnum.next && this._paginationNavigation.previousPage.size() === 0;

      if (pageUrl) {
        this._fetchPageWhenTokenExist(pageUrl);
      }
    }
  }

  public onColumnSortClick(sortData: SortColumn): void {
    this.pageFilters.sortedColumn$.next(sortData);
  }

  public multiSelectedActionClick(menuAction: MenuAction): void {
    const dialogRef = this.matDialog.open(ChangeEventJustificationDialogComponent, {
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });

    dialogRef.afterClosed().subscribe((justificationOutput?: IChangeEventJustificationComponentOutput) => {
      if (justificationOutput && justificationOutput.addJustification && justificationOutput.content) {
        const violationIdsByIntegration = _.groupBy(
          Object.values(this._currentViolationsById).filter((violation) => this.checkedViolationIds.has(violation.id)),
          (violation: ViolationModel) => violation.integration.id
        );
        const status = menuAction.name as ViolationsSupportedActions;

        if (!this.expandFilterResultControl.value) {
          if (status == ViolationsSupportedActions.Exempt) {
            this._updateCheckedViolationsStatus(
              UpdatableViolationStatus.exempted,
              violationIdsByIntegration,
              justificationOutput.content
            );
          } else if (status == ViolationsSupportedActions.Waive) {
            this._resetCheckedViolationsStatus(violationIdsByIntegration, justificationOutput.content);
          } else {
            throw new Error(`ViolationsComponent multiSelectedActionClick: The status ${status} is not supported`);
          }
        } else {
          const statusAction = getViolationStatusBulkActionType(status);
          this.multiActionsIsLoading$.next(true);
          const bulkModel = {
            dataType: ViolationsDataType.violations,
            change_reason: {
              justification: justificationOutput.content,
            } as ViolationChangeJustificationApiModel,
          } as ViolationsActionBulkApiModel;

          this._violationsHttpService
            .applyBulkAction(bulkModel, statusAction, this.pageFilters.getSelectedFilters(), this.sorts)
            .subscribe({
              next: () => {
                this.multiActionsIsLoading$.next(false);
                this._notificationService.success(
                  `Bulk action was submitted, an email notification will be sent upon completion.`
                );
              },
              error: (error) => {
                this.multiActionsIsLoading$.next(false);
                this._httpErrorHandlerService.handle(error, true);
              },
            });
        }
      }
    });
  }

  public onCheckboxClick(event: Set<string>): void {
    this.checkedViolationIds = event;
    this._updateSupportedActionsForSelectedRows();
  }

  public columnsSelected(selectedColumns: ITableColumn[]) {
    this.columns.set(structuredClone(selectedColumns));
  }

  private _updateSupportedActionsForSelectedRows(): void {
    const menuActions: MenuAction[] = [];
    let atLeastOneViolationOpen = false;
    let atLeastOneViolationCustomStatus = false;
    let userHasPermissionsForAllSelected = true;

    this.checkedViolationIds.forEach((violationId) => {
      const violation = this._currentViolationsById[violationId];
      if (violation.status === ViolationStatus.open) {
        atLeastOneViolationOpen = true;
      }
      if (violation.status !== ViolationStatus.open && violation.status !== ViolationStatus.resolved) {
        atLeastOneViolationCustomStatus = true;
      }
      // Check permission per violation's integration ID
      if (!this._permissionsService.userHasPermission(PERMISSIONS.VIOLATION_OPERATION, violation.integration.id)) {
        userHasPermissionsForAllSelected = false;
      }
    });

    const notSupportedTooltip = 'Action not applicable';
    const missingPermissionsTooltip = this._labelsProviderPipe.transform('commonActions.operatorActionTooltip');
    let exemptTooltip = notSupportedTooltip;
    let waiveTooltip = notSupportedTooltip;

    if (!userHasPermissionsForAllSelected) {
      exemptTooltip = waiveTooltip = missingPermissionsTooltip;
    }

    const tooltip = this.expandFilterResultControl.value ? 'Resolved violations are not applicable' : '';

    menuActions.push({
      name: ViolationsSupportedActions.Exempt.toString(),
      displayNameLabel: 'violationActions.exempt',
      disabled: !(
        userHasPermissionsForAllSelected &&
        (this.expandFilterResultControl.value || atLeastOneViolationOpen)
      ),
      tooltip: !(userHasPermissionsForAllSelected && (this.expandFilterResultControl.value || atLeastOneViolationOpen))
        ? exemptTooltip
        : tooltip,
    } as MenuAction);

    menuActions.push({
      name: ViolationsSupportedActions.Waive.toString(),
      displayNameLabel: 'violationActions.waive',
      disabled: !(
        userHasPermissionsForAllSelected &&
        (this.expandFilterResultControl.value || atLeastOneViolationCustomStatus)
      ),
      tooltip: !(
        userHasPermissionsForAllSelected &&
        (this.expandFilterResultControl.value || atLeastOneViolationCustomStatus)
      )
        ? waiveTooltip
        : tooltip,
    } as MenuAction);

    this.multiSelectedMenuActions$.next(menuActions);
  }

  private _onAllRowsSelectionClick(newValue: boolean | null): void {
    const newBooleanValue = newValue ? true : false;
    const currentViolations = this.violationsCurrentPage$.getValue();
    const updatedViolations = currentViolations.map((violation) => {
      violation.checkboxStatus = newBooleanValue;
      newBooleanValue ? this.checkedViolationIds.add(violation.id) : this.checkedViolationIds.delete(violation.id);
      return violation;
    });

    this._storeService.setViolationsPageData(updatedViolations);

    this._updateSupportedActionsForSelectedRows();
  }

  private _subscribeToFiltersAndSortChanges(): void {
    // Wait for ALL async filter options to load from the store before subscribing
    // Each can trigger onUpdate$, so we must wait for all to prevent premature API calls
    combineLatest([
      this._storeService.integrations$,
      this._storeService.accountMinimalPolicies$,
      this._storeService.baseViolationRule$,
      this._storeService.labels$,
    ])
      .pipe(
        switchMap(() => combineLatest([this.pageFilters.onUpdate$, this.pageFilters.sortedColumn$])),
        debounceTime(0),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(([selectedFilters, sortColumn]) => {
        this.optionsAreSelected = this.pageFilters.getAllSelectedOptions().length > 0;
        this._fetchFirstPage(selectedFilters, sortColumn);
      });
  }

  private _fetchByPage(continuationToken: string): void {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    this._fetchByPageSubscription$ = this._violationsHttpService
      .getViolationsByContinuationToken(continuationToken)
      .subscribe({
        next: (response) => this._handleViolationCollectionResponse(response),
        error: (error) => this._fetchRequestOnError(error),
      });
  }

  private _fetchFirstPage(filterOptions: ISelectedFilters, sort?: SortColumn): void {
    this._isOnFirstPage = true;
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    if (sort) {
      this.sorts = [sort];
    }

    this._firstPageSubscription$ = this._violationsHttpService.getViolations(filterOptions, this.sorts).subscribe({
      next: (response) => {
        this.allRowsSelected.setValue(false);
        return this._handleViolationCollectionResponse(response);
      },
      error: (error) => this._fetchRequestOnError(error),
    });
  }

  private _cancelOngoingHttpRequest(): void {
    if (this._firstPageSubscription$) {
      this._firstPageSubscription$.unsubscribe();
    }

    if (this._fetchByPageSubscription$) {
      this._fetchByPageSubscription$.unsubscribe();
    }
  }

  private _fetchRequestOnError(httpError: HttpErrorResponse): void {
    this._storeService.setViolationsPageData([]);
    this.isLoading$.next(false);
    this.showNoData = true;
    this._httpErrorHandlerService.handle(httpError);
  }

  private _tableBehaviorChangeBeforeFetch(): void {
    this._storeService.setViolationsPageData([]);
    this.isLoading$.next(true);
    this.checkedViolationIds.clear();
  }

  private _handleViolationCollectionResponse(response: JsonHalCollection<ViolationModel>): void {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);

    const violations = response._embedded.items;
    const viewModels: IViolationsViewModel[] = [];

    this._currentViolationsById = {};
    if (violations?.length) {
      violations.forEach((violation) => {
        this._currentViolationsById[violation.id] = violation;
        viewModels.push(this._transformViolationResponse(violation));
      });
    }

    this._loadDemoMockViolations(violations ?? [], viewModels);
  }

  private _handlePaginationUrls(links: JsonHalLinks): void {
    if (!this._paginationNavigation) {
      this._paginationNavigation = {
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel;
    } else {
      this._paginationNavigation.currentPage = links.self.href;
      this._paginationNavigation.nextPage = links.next?.href;
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo): void {
    this.totalItems = pageInfo.totalItems;
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize);

    if (!this.pageInfo$) {
      this.pageInfo$ = new BehaviorSubject(tablePageInfo);
    } else {
      this.pageInfo$.next(tablePageInfo);
    }
  }

  private _transformViolationResponse(violation: ViolationModel): IViolationsViewModel {
    const platform = violation.resource.platformInfo.platform;
    const errorMessage = populateErrorMessage(violation.resource);
    const createdByDisplayName = this._displayNameService.getCreatedByDisplayName(
      violation.resource?.identities?.createdBy,
      violation.resource.resourceAdditionalProperties
    );
    const ownerDisplayNames = violation.resource.identities?.ownedBy?.value
      ? violation.resource.identities.ownedBy.value
          .map((owner) => owner.displayName)
          .filter((displayName) => displayName)
      : [];
    return {
      rowId: violation.id,
      id: violation.id,
      name: violation.rule.displayName,
      toolTipViolationName: errorMessage != '' ? errorMessage : null,
      iconSrcViolationName: 'assets/images/status-icons/error.svg',
      labels: violation.labelBindings,
      resourceName: violation.resource.displayName,
      resourceType: buildResourceTypeDisplayName(violation.resource.resourceType),
      createdBy: createdByDisplayName,
      notApplicableCreatedByTooltipText:
        this._textProviderService.getNotApplicableTableTooltipTextByResourceTypeIfNeeded(
          violation.resource.resourceType,
          FieldName.createdBy,
          createdByDisplayName
        ),
      ownedBy: ownerDisplayNames,
      notApplicableOwnedByTooltipText: this._textProviderService.getNotApplicableTableTooltipTextByResourceTypeIfNeeded(
        violation.resource.resourceType,
        FieldName.ownedBy,
        ownerDisplayNames[0]
      ),
      resourceTypeIcon: getResourceCategoryIconPath(violation.resource.resourceCategory),
      platform: platform,
      platformIcon: violation.resource.aiService
        ? getAiServiceLogoPath(violation.resource.aiService)
        : getPlatformLogoPath(platform),
      resourceStatusLabel: InventoryHelper.getViolationRelatedResourceStatusLabel(
        violation.resource,
        violation.statusReason
      ),
      owasp: owaspLcncToDisplayName[violation.rule.owasp[0]],
      severity: violation.rule.severity.toString(),
      firstSeen: formatFromDate(violation.firstSeen),
      statusLabel: ViolationRowHelpers.getStatusLabel(violation.status),
      menuActions: [],
      clickable: true,
      resourceId: violation.resource.platformInfo.resourceId,
      ruleId: violation.rule.id,
      integrationId: violation.integration.id,
      ruleTags: violation.rule.tags,
      violation: violation,
    } as IViolationsViewModel;
  }

  private _fetchPageWhenTokenExist(url: string): void {
    const continuationToken = this._tokenExtractor(url);

    if (continuationToken) {
      this._fetchByPage(continuationToken);
    }
  }

  private _tokenExtractor(url: string): string | null {
    const continuationToken = new URLSearchParams(url.slice(url.indexOf('?'))).get('continuationToken');
    if (!continuationToken) {
      this.logger.error('Failed to extract continuation token from json hal next inventory resource page link');
      return null;
    }

    return continuationToken;
  }

  private _initializePageInfo(): void {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 0,
      maxSize: this.defaultPageSize,
      totalItems: 0,
      totalPages: 1,
    });
  }

  private _handleViolationStatusUpdateActionResponse(
    actionResponse: Observable<ViolationsStatusUpdateResponse | undefined>,
    rowActionClicked: RowActionClicked
  ): void {
    const violationRow = rowActionClicked.row.getValue() as IViolationsViewModel;
    this._currentViolationsById[violationRow.id];

    actionResponse.subscribe(
      (response: ViolationsStatusUpdateResponse | undefined) => {
        if (response) {
          const notificationText = 'Violation was updated, refreshing page';
          this._notificationService.success(notificationText);
          this._refreshCurrentPage();
        }
      },
      (error) => this._notificationService.error(error.error.detail)
    );
  }

  private _handleResourceDeleteActionResponse(actionResponse: Observable<boolean>): void {
    actionResponse.subscribe((resp) => {
      if (resp) {
        this._refreshCurrentPage();
      }
    });
  }

  private _refreshCurrentPage(): void {
    const continuationToken = this._tokenExtractor(this._paginationNavigation.currentPage);
    if (continuationToken) {
      this._fetchByPage(continuationToken);
    }
  }

  private _resetCheckedViolationsStatus(
    violationsByIntegration: { [integrationId: string]: ViolationModel[] },
    justificationContent: string
  ): void {
    const requests = Object.keys(violationsByIntegration).map((integrationId) => {
      const resetModel = {
        ids: violationsByIntegration[integrationId].map((violation) => violation.id),
        reset_reason: {
          justification: justificationContent,
        } as ViolationChangeJustificationApiModel,
      } as ResetViolationsStatusApiModel;

      return this._violationsHttpService.resetViolationStatus(integrationId, resetModel);
    });

    this.multiActionsIsLoading$.next(true);
    forkJoin(requests).subscribe({
      next: () => {
        this._handleMultiResponse(true);
      },
      error: () => {
        this._handleMultiResponse(false);
      },
    });
  }

  private _updateCheckedViolationsStatus(
    newStatus: UpdatableViolationStatus,
    violationsByIntegration: { [integrationId: string]: ViolationModel[] },
    justificationContent: string
  ): void {
    const requests = Object.keys(violationsByIntegration).map((integrationId) => {
      const updateModel = {
        ids: violationsByIntegration[integrationId].map((violation) => violation.id),
        status: newStatus,
        change_reason: {
          justification: justificationContent,
        } as ViolationChangeJustificationApiModel,
      } as UpdateViolationsStatusApiModel;

      return this._violationsHttpService.updateViolationStatus(integrationId, updateModel);
    });

    this.multiActionsIsLoading$.next(true);
    forkJoin(requests).subscribe({
      next: () => {
        this._handleMultiResponse(true);
      },
      error: () => {
        this._handleMultiResponse(false);
      },
    });
  }

  private _handleMultiResponse(succeedResponse: boolean): void {
    this.multiActionsIsLoading$.next(false);
    if (succeedResponse) {
      this._notificationService.success('Status updated, refreshing page');
    } else {
      this._notificationService.warning('Partially updated, refreshing page');
    }
    this.checkedViolationIds = new Set();
    this.allRowsSelected.setValue(false);
    this._refreshCurrentPage();
  }

  private _isRsaDemoEnabled(): boolean {
    return this.featureFlagsService.isFeatureEnabled(
      FeatureFlagTypes.RsaDemoRuntimeBoundaries
    );
  }

  private _isIamDemoEnabled(): boolean {
    return this.featureFlagsService.isFeatureEnabled(
      FeatureFlagTypes.SpmIamDemoAmex
    );
  }

  private _applyDynamicViolationTimestamps(
    violations: ViolationModel[]
  ): ViolationModel[] {
    const now = new Date();
    const base = new Date(now);
    base.setMinutes(now.getMinutes() - 5);

    return violations.map((v, i) => {
      const offset = i * 15 * 1000;
      const ts = new Date(base.getTime() - offset);
      return {
        ...v,
        firstSeen: ts,
        lastSeen: ts,
      } as ViolationModel;
    });
  }

  private _loadDemoMockViolations(
    apiViolations: ViolationModel[],
    apiViewModels: IViolationsViewModel[]
  ): void {
    const anyDemoEnabled =
      this._isRsaDemoEnabled() || this._isIamDemoEnabled();
    if (!this._isOnFirstPage || !anyDemoEnabled) {
      this._finalizeViolationsPage(apiViolations, apiViewModels);
      return;
    }

    const rsaCached = !this._isRsaDemoEnabled() ||
      this._rsaMockViolations !== null;
    const iamCached = !this._isIamDemoEnabled() ||
      this._iamDemoMockViolations !== null;

    if (rsaCached && iamCached) {
      this._prependMockViolations(apiViolations, apiViewModels);
      return;
    }

    const rsaMock$ = this._isRsaDemoEnabled() &&
      !this._rsaMockViolations
      ? this._httpClient.get<Record<string, ViolationModel>>(
          'assets/mockData/rsaViolationsMockData.json'
        )
      : of(null);

    const iamMock$ = this._isIamDemoEnabled() &&
      !this._iamDemoMockViolations
      ? this._httpClient.get<ViolationModel[]>(
          'assets/mockData/iamDemoViolationsMockData.json'
        )
      : of(null);

    forkJoin([rsaMock$, iamMock$]).subscribe({
      next: ([rsaData, iamData]) => {
        if (rsaData) {
          this._rsaMockViolations = Object.values(rsaData);
        }
        if (iamData) {
          this._iamDemoMockViolations =
            this._applyDynamicViolationTimestamps(iamData);
        }
        this._prependMockViolations(apiViolations, apiViewModels);
      },
      error: () =>
        this._finalizeViolationsPage(apiViolations, apiViewModels),
    });
  }

  private _prependMockViolations(
    apiViolations: ViolationModel[],
    apiViewModels: IViolationsViewModel[]
  ): void {
    const filters = this.pageFilters.getSelectedFilters();
    const selectedPlatforms = new Set(filters.platform ?? []);
    const hasPlatformFilter = selectedPlatforms.size > 0;

    const allMocks = [
      ...(this._iamDemoMockViolations ?? []),
      ...(this._rsaMockViolations ?? []),
    ];

    let filtered = allMocks;
    if (hasPlatformFilter) {
      filtered = filtered.filter((v) =>
        selectedPlatforms.has(v.resource.platformInfo.platform)
      );
    }

    this._mockViolationIds = new Set(filtered.map((v) => v.id));
    const mockViewModels = filtered.map((v) => {
      this._currentViolationsById[v.id] = v;
      return this._transformViolationResponse(v);
    });

    const merged = [...mockViewModels, ...apiViewModels].sort(
      (a, b) =>
        new Date(b.firstSeen).getTime() -
        new Date(a.firstSeen).getTime()
    );
    this._finalizeViolationsPage(apiViolations, merged);
  }

  private _finalizeViolationsPage(apiViolations: ViolationModel[], viewModels: IViolationsViewModel[]): void {
    if (viewModels.length === 0) {
      this.showNoData = true;
      this._storeService.setViolationsPageData([]);
      this.isLoading$.next(false);
      return;
    }

    this.showNoData = false;
    const isMock = (id: string) =>
      id.startsWith('rsa-') || this._mockViolationIds.has(id);
    const realViolations = apiViolations.filter(
      (v) => !isMock(v.id)
    );

    if (realViolations.length > 0) {
      this._violationsActionsService
        .setActionsForViolations(
          realViolations,
          viewModels.filter((vm) => !isMock(vm.id))
        )
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          complete: () => {
            this._storeService.setViolationsPageData(viewModels);
            this.isLoading$.next(false);
          },
        });
    } else {
      this._storeService.setViolationsPageData(viewModels);
      this.isLoading$.next(false);
    }
  }
}
